import React from "react";
import { t, Trans } from "@lingui/macro";
import { Switch } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/solid";
import { Button } from "components/UI/Button/Button";
import { useInputChange } from "hooks/useInputChange";
import { useDispatch, useSelector } from "react-redux";
import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { InputText } from "components/UI/InputText/InputText";
import { SelectBox } from "../../../../../UI/SelectBox/SelectBox";
import { vatList } from "../../../../../../constants/vatAllowedValue";
import { Autocomplete } from "../../../../../UI/Autocomplete/Autocomplete";
import { selectPrimaryTenant } from "../../../../../../model/system/selectors";
import { addNotificationAC } from "../../../../../../model/system/systemActionCreators";
import { useInputChangeNumberOnly } from "../../../../../../hooks/useInputChangeNumberOnly";
import { IdProductsStateDataItem } from "../../../../../../model/dashboardProducts/dProductsReducer";
import { dEdmProductsAddItemSagaAC } from "../../../../../../model/dashboardEdmProducts/dEdmProductsActionCreators";
import {
  selectFilters,
  selectPaginationObject,
} from "../../../../../../model/dashboardEdmProducts/selectors";
import {
  checkExistPSID,
  getProductList,
} from "../../../../../../model/dashboardProducts/dProductsAPI";

export type typePopupAdd = {
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export type typeAddEdmProductsItemObject = {
  price: number;
  units: string;
  amount: number;
  totalSum: number;
  vatRate: number | null;
  markedLabels?: string[];
  supplierId: string | null;
  product: {
    name: string;
    psid: string;
    marked: boolean;
    packageCode: string;
    productOwnerType: string;
    barcodes: string[];
  };
};

export const PopupAdd: React.FC<typePopupAdd> = ({ setOpenPopup }) => {
  const dispatch = useDispatch();

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [product, setProduct] = React.useState<
    IdProductsStateDataItem | undefined
  >();

  const typeOfProduct = [
    {
      id: "TOBACCO_PRODUCTS",
      name: t`Tobacco products`,
    },
    {
      id: "ALCOHOLIC_PRODUCTS",
      name: t`Alcoholic beverages (except brewing products)`,
    },
    {
      id: "BEER_PRODUCTS",
      name: t`Brewing products`,
    },
    {
      id: "MEDICINAL_PRODUCTS",
      name: t`Medicinal products for medical use`,
    },
    {
      id: "WATER_AND_COLD_DRINKS",
      name: t`Water and soft drinks`,
    },
    {
      id: "APPLIANCES",
      name: t`Appliances`,
    },
  ];

  const typeOfPackaging = [
    {
      id: "CONSUMER",
      name: t`Consumer`,
    },
    {
      id: "GROUP",
      name: t`Group`,
    },
    {
      id: "TRANSPORT",
      name: t`Transport`,
    },
  ];

  const { value: count, changeValueHandler: countHandler } =
    useInputChangeNumberOnly("", true);

  const { value: price, changeValueHandler: priceHandler } =
    useInputChangeNumberOnly("", true);

  const { value: totalSum, changeValueHandler: totalSumHandler } =
    useInputChangeNumberOnly("", true);

  const {
    value: barcodeValue,
    setValue: setBarcodeValue,
    changeValueHandler: barcodeChangeValueHandler,
  } = useInputChange("");

  const filters = useSelector(selectFilters);
  const primaryTenant = useSelector(selectPrimaryTenant);
  const paginationObject = useSelector(selectPaginationObject);

  const [productId, setProductId] = React.useState<any>("");
  const [barcodeList, setBarcodeList] = React.useState<string[]>([]);
  const [supplierId, setSupplierId] = React.useState<string | null>(null);
  const [isOpenSearchResult, toggleSearchResult] =
    React.useState<boolean>(false);
  const [vatValue, setVatValue] = React.useState<number | null>(null);
  const [suppliersList] = React.useState<{ id: string; name: string }[]>([]);

  const [productTypeId, setProductTypeId] = React.useState<string | null>(null);
  const [packagingTypeId, setPackagingTypeId] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    if (count) {
      setErrors((prevState) => ({ ...prevState, count: "" }));
    }
  }, [count]);

  React.useEffect(() => {
    if (price) {
      setErrors((prevState) => ({ ...prevState, price: "" }));
    }
  }, [price]);

  React.useEffect(() => {
    if (totalSum) {
      setErrors((prevState) => ({ ...prevState, totalSum: "" }));
    }
  }, [totalSum]);

  React.useEffect(() => {
    if (productId) {
      setErrors((prevState) => ({ ...prevState, productName: "" }));
    }
  }, [productId]);

  React.useEffect(() => {
    if (barcodeList.length) {
      setErrors((prevState) => ({ ...prevState, barcode: "" }));
    }
  }, [barcodeList]);

  React.useEffect(() => {
    if (productTypeId) {
      setErrors((prevState) => ({ ...prevState, productTypeId: "" }));
    }
  }, [productTypeId]);

  React.useEffect(() => {
    if (packagingTypeId) {
      setErrors((prevState) => ({ ...prevState, packagingTypeId: "" }));
    }
  }, [packagingTypeId]);

  const onAddBarcodeHandler = () => {
    if (barcodeValue.trim() !== "") {
      const newList: string[] = [...barcodeList];
      const listItem = newList?.find((item) => item === barcodeValue);

      if (listItem === undefined) {
        newList.push(barcodeValue.trim());
        setBarcodeList(newList);
        setBarcodeValue("");
      } else {
        setErrors((prevState) => ({
          ...prevState,
          barcode: t`This barcode already exists`,
        }));
      }
    }
  };

  const onAddHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    const product = searchResult.find((item) => item.id === productId);

    let invalidInputs: Record<string, string> | undefined;

    if (!productId?.trim()) {
      invalidInputs = {
        ...(invalidInputs || {}),
        productName: t`Required field`,
      };
    }

    if (!count) {
      invalidInputs = {
        ...(invalidInputs || {}),
        count: t`Required field`,
      };
    }

    if (!price) {
      invalidInputs = {
        ...(invalidInputs || {}),
        price: t`Required field`,
      };
    }

    if (!totalSum) {
      invalidInputs = {
        ...(invalidInputs || {}),
        totalSum: t`Required field`,
      };
    }

    if (product?.marked) {
      if (!productTypeId) {
        invalidInputs = {
          ...(invalidInputs || {}),
          productTypeId: t`Required field`,
        };
      }

      if (!packagingTypeId) {
        invalidInputs = {
          ...(invalidInputs || {}),
          packagingTypeId: t`Required field`,
        };
      }

      if (!barcodeList.length) {
        invalidInputs = {
          ...(invalidInputs || {}),
          barcode: t`Required field`,
        };
      }
    }

    if (invalidInputs) {
      setErrors((prevState) => ({
        ...prevState,
        ...invalidInputs,
      }));

      return;
    }

    let productType = {};
    let packageType = {};

    if (product?.marked) {
      productType = {
        productType: productTypeId,
      };
      packageType = {
        packageType: packagingTypeId,
      };
    }

    const res = await checkExistPSID({ psid: product?.psid || "" });

    if (res.status !== 200) {
      const id = new Date();
      dispatch(
        addNotificationAC({
          id: id.getTime().toString(),
          message: t`Incorrect PSID. Please make sure that the PSID is correct and try again.`,
        }),
      );

      return;
    }

    const data: typeAddEdmProductsItemObject = {
      price: Number(price),
      amount: Number(count),
      vatRate: vatValue,
      totalSum: Number(totalSum),
      units: product?.units || "",
      supplierId: supplierId || null,
      markedLabels: barcodeList || [],
      ...packageType,
      product: {
        name: product?.name || "",
        marked: !!product?.marked,
        psid: product?.psid || "",
        packageCode: product?.packageCode || "",
        productOwnerType: product?.productOwnerType || "",
        barcodes: product?.barcodes || [],
        ...productType,
      },
    };

    dispatch(
      dEdmProductsAddItemSagaAC(data, {
        filters,
        paginationRequest: {
          page: paginationObject.pageNumber,
          size: paginationObject.itemsPerPage!,
        },
        primaryTenant: primaryTenant!,
      }),
    );

    setOpenPopup(false);
  };

  const onDeleteBarcodeHandler = (barcode: string) => {
    const newList: string[] = [...[], ...barcodeList];

    const listItemIndex = newList.findIndex((item) => item === barcode);

    if (listItemIndex >= 0) newList.splice(listItemIndex, 1);

    setBarcodeList(newList);
  };

  const onEnterBarcode = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();

      onAddBarcodeHandler();
    }
  };

  const {
    value: searchValue,
    setValue: setSearchValue,
    changeValueHandler: onChangeSearchValue,
  } = useInputChange("");

  const [loading, setLoading] = React.useState<boolean>(false);
  const [searchResult, setSearchResult] = React.useState<
    IdProductsStateDataItem[]
  >([]);

  React.useEffect(() => {
    (async () => {
      // const res = await getSuppliersList();
      //
      // setSuppliersList(
      //   res.data.map((item: any) => ({
      //     id: item.id,
      //     name: item.name,
      //   }))
      // );
    })();
  }, []);

  React.useEffect(() => {
    if (!searchValue?.trim()) {
      (async () => {
        const res = await getProductList({
          paginationRequest: {
            page: 0,
            size: 100,
          },
          filters: { deleted: false },
        });

        setSearchResult(res?.data?.content || []);
      })();
    }
  }, [searchValue]);

  const onEnterSearch = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    setLoading(true);

    if (searchValue.trim()) {
      const res = await getProductList({
        filters: {
          deleted: false,
          name: searchValue.trim(),
        },
        paginationRequest: {
          page: 0,
          size: 100,
        },
      });

      setSearchResult(res?.data?.content || []);

      toggleSearchResult(true);
    } else {
      setSearchResult([]);
    }

    setLoading(false);
  };

  return (
    <div style={{ width: 792 }}>
      <style>{`.w-52 { min-width: 13rem  }`}</style>

      <form onSubmit={(e) => onAddHandler(e)} noValidate>
        <div className="relative">
          <div className="relative flex gap-4 flex-col">
            <div className="relative flex flex-row items-center gap-6 w-full">
              {isOpenSearchResult && (
                <div
                  onClick={() => toggleSearchResult(false)}
                  className="fixed top-0 left-0 w-full h-full z-20"
                />
              )}
              <div className="flex flex-row items-center gap-28 w-full">
                <Autocomplete
                  name="name"
                  id="productName"
                  variant="variant"
                  required={true}
                  autoComplete="off"
                  loading={loading}
                  value={searchValue}
                  inputClassName="w-full"
                  onFocus={() => {
                    if (!!searchResult?.length) {
                      toggleSearchResult(true);
                    }
                  }}
                  onBlur={() => {
                    if (product) {
                      setSearchValue(product.name);
                    }
                  }}
                  asteriskPlacement="right"
                  inputWrapClassName="w-full"
                  onChange={onChangeSearchValue}
                  onKeyEnterDown={onEnterSearch}
                  label={<Trans>Product</Trans>}
                  errorMessage={errors.productName}
                  placeholder={t`Select a product from the directory`}
                  labelClassName="text-left whitespace-nowrap text-md font-bold w-52"
                  helper={
                    <Trans>
                      Labeling can be added by scanning the code of each product
                    </Trans>
                  }
                />
              </div>

              {isOpenSearchResult && (
                <div className="top-12 z-30 w-full flex flex-row gap-28 absolute right-0">
                  <div className="w-52" />

                  <div
                    style={{ maxHeight: 300 }}
                    className="bg-white py-1 border rounded-md shadow-md w-full overflow-y-auto"
                  >
                    {!!searchResult?.length ? (
                      searchResult.map((item) => {
                        const active = item.id === productId;

                        return (
                          <div
                            key={item.id}
                            onClick={() => {
                              setProduct(item);
                              setProductId(item.id);
                              setSearchValue(item.name);
                              toggleSearchResult(false);

                              setBarcodeList([]);
                              setBarcodeValue("");
                              setProductTypeId(null);
                              setPackagingTypeId(null);
                            }}
                            className={`${
                              active ? "bg-primary-600 text-white" : "bg-white"
                            } p-2 hover:text-white hover:bg-primary-600 cursor-pointer`}
                          >
                            <div className="flex gap-1">
                              <span
                                className={active ? "text-white" : "opacity-0"}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                              {item.name}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div>
                        <div className="text-center text-gray-400">
                          No results
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-row items-center gap-28 w-full">
              <InputText
                required
                id="count"
                type="text"
                value={count}
                autoComplete="off"
                asteriskPlacement="right"
                onChange={countHandler}
                inputWrapClassName="w-full"
                placeholder={t`Enter the quantity`}
                label={<Trans>Count of products</Trans>}
                labelClassName="text-left whitespace-nowrap text-md font-bold w-52"
                errorMessage={errors.count ? <>{errors.count}</> : undefined}
              />
            </div>

            <div className="relative flex flex-row items-center gap-28 w-full">
              <InputText
                required
                type="text"
                value={price}
                id="pricePerUnit"
                autoComplete="off"
                inputClassName="pr-12"
                onChange={priceHandler}
                asteriskPlacement="right"
                inputWrapClassName="w-full"
                placeholder={t`Specify the price`}
                label={<Trans>Price per unit</Trans>}
                labelClassName="text-left whitespace-nowrap text-md font-bold w-52"
                errorMessage={errors.price ? <>{errors.price}</> : undefined}
              />
              <span
                style={{ color: "#D1D5DB", height: 38 }}
                className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                UZS
              </span>
            </div>

            <div className="flex flex-row items-center gap-28 w-full">
              <SelectBox
                id="vat"
                valueId={vatValue}
                valuesList={vatList}
                setIdValue={setVatValue}
                selectBosClassname="w-full"
                placeholder={t`Select VAT`}
                label={<Trans>VAT percentage</Trans>}
                labelClassname="text-left whitespace-nowrap text-md font-bold w-52"
              />
            </div>

            <div className="relative flex flex-row items-center gap-28 w-full">
              <InputText
                required
                type="text"
                name="price"
                id="totalSum"
                autoComplete="off"
                value={totalSum}
                inputClassName="pr-12"
                asteriskPlacement="right"
                inputWrapClassName="w-full"
                onChange={totalSumHandler}
                placeholder={t`Specify the price`}
                label={<Trans>Purchase price</Trans>}
                labelClassName="text-left whitespace-nowrap text-md font-bold w-52"
                errorMessage={
                  errors.totalSum ? <>{errors.totalSum}</> : undefined
                }
              />
              <span
                style={{ color: "#D1D5DB", height: 38 }}
                className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                UZS
              </span>
            </div>

            <div className="hidden flex-row items-center gap-28 w-full">
              <SelectBox
                withoutEmpty
                id="provider"
                valueId={supplierId}
                selectBosClassname="w-full"
                setIdValue={setSupplierId}
                valuesList={suppliersList}
                label={<Trans>Provider</Trans>}
                placeholder={t`Specify the supplier`}
                labelClassname="text-left whitespace-nowrap text-md font-bold w-52"
              />
            </div>

            <div
              style={{ borderBottom: "1px solid #E5E7EB" }}
              className="divide-y w-full"
            />

            <div className="flex flex-row items-center w-full gap-28">
              <label className="text-sm text-gray-700 text-md items-center text-left whitespace-nowrap text-md font-bold flex flex-row gap-2 w-52">
                <Trans>Product labeling</Trans>
                <Switch
                  checked={!!product?.marked}
                  className={`${
                    product?.marked ? "bg-primary-600" : "bg-gray-200"
                  } relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${
                      product?.marked ? "translate-x-6" : "translate-x-1"
                    } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                  />
                </Switch>
              </label>
              {product?.marked && (
                <div className="w-full">
                  <SelectBox
                    id="productTypeId"
                    disableLabel={true}
                    valueId={productTypeId}
                    selectBosClassname="w-full"
                    valuesList={typeOfProduct}
                    setIdValue={setProductTypeId}
                    placeholder={t`Choose the type of product`}
                    labelClassname="text-left whitespace-nowrap text-md font-bold w-52"
                    errorMessage={
                      errors.productTypeId ? (
                        <>{errors.productTypeId}</>
                      ) : undefined
                    }
                  />
                </div>
              )}
            </div>
            <div className="flex flex-row items-center w-full gap-28">
              <label className="text-sm text-gray-700 text-md items-center text-left whitespace-nowrap text-md font-bold flex flex-row gap-2 w-52" />
              {product?.marked && (
                <div className="w-full">
                  <SelectBox
                    id="packagingTypeId"
                    disableLabel={true}
                    selectBosClassname="w-full"
                    valueId={packagingTypeId}
                    valuesList={typeOfPackaging}
                    setIdValue={setPackagingTypeId}
                    placeholder={t`Choose the type of packaging`}
                    labelClassname="text-left whitespace-nowrap text-md font-bold w-52"
                    errorMessage={
                      errors.packagingTypeId ? (
                        <>{errors.packagingTypeId}</>
                      ) : undefined
                    }
                  />
                </div>
              )}
            </div>
            {product?.marked && (
              <div className="flex flex-col gap-2">
                <div className="pt-2 flex flex-wrap gap-2 items-end">
                  <div className="relative flex flex-row items-center gap-28 w-full">
                    <InputText
                      required
                      id="barcode"
                      type="text"
                      autoComplete="off"
                      value={barcodeValue}
                      asteriskPlacement="right"
                      inputWrapClassName="w-full"
                      label={<Trans>Marking codes</Trans>}
                      onChange={barcodeChangeValueHandler}
                      onKeyDown={(e) => onEnterBarcode(e)}
                      labelClassName="text-left whitespace-nowrap text-md font-bold w-52"
                      errorMessage={
                        errors.barcode ? <>{errors.barcode}</> : undefined
                      }
                    />
                  </div>
                  <div className="relative flex flex-row items-center gap-28 w-full">
                    <div className="text-left whitespace-nowrap text-md font-bold w-52"></div>
                    <div className="flex flex-col w-full">
                      <Button
                        type="button"
                        secondary={true}
                        buttonClassName="w-full"
                        disabled={!barcodeValue}
                        onClick={onAddBarcodeHandler}
                        label={<Trans>Add a marking code</Trans>}
                        icon={<PlusIcon className="w-5 h-5 mr-1" />}
                      />
                      <p
                        style={{ color: "#6B7280" }}
                        className="italic mb-0 mt-1 px-4 text-xs text-center"
                      >
                        <Trans>
                          Labeling can be added by scanning the code of each
                          product
                        </Trans>
                      </p>
                    </div>
                  </div>
                  {!!(barcodeList && barcodeList.length) && (
                    <div className="relative flex flex-row items-center gap-28 w-full">
                      <div className="text-left whitespace-nowrap text-md font-bold w-52"></div>
                      <div className="flex flex-col gap-2 py-3 w-full">
                        {barcodeList.map((item, index) => (
                          <div
                            key={item + index}
                            className="flex gap-x-1 items-center px-4 py-2 rounded-md"
                            style={{
                              backgroundColor: "#F3F4F6",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <span
                              style={{ color: "#6B7280" }}
                              className="flex-grow text-left text-md"
                            >
                              {item}
                            </span>
                            <span>
                              <div
                                className="cursor-pointer"
                                onClick={() => onDeleteBarcodeHandler(item)}
                              >
                                <XIcon className="h-5 w-5 " />
                              </div>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-4 mt-8 sm:flex sm:flex-row-reverse">
          <Button type="submit" label={<Trans>Add</Trans>} />
          <Button
            type="button"
            secondary={true}
            label={<Trans>Cancel</Trans>}
            onClick={() => setOpenPopup(false)}
          />
        </div>
      </form>
    </div>
  );
};
